/**
 * @graphql cc
 */
import React, { useCallback, useRef } from 'react';

import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Box } from '@atlaskit/primitives/compiled/box';

import {
	useObjectCommentDispatchContext,
	useObjectCommentContext,
} from '@confluence/comment-context/entry-points/ObjectCommentContext.ts';
import { AddFooterCommentMode } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component.ts';
import { fg } from '@confluence/feature-gating';
import { LoadablePaint } from '@confluence/loadable';
import { usePageContentId } from '@confluence/page-context';

import { ObjectAddCommentPrompt } from './ObjectAddCommentPrompt.tsx';
import { ObjectCommentPageReactions } from './ObjectCommentPageReactions.tsx';

const FooterLabelsLoader = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-FooterLabels" */ '@confluence/labels-footer'))
			.FooterLabels,
});

const styles = cssMap({
	addCommentContainerStyles: {
		position: 'sticky',
		bottom: token('space.0'),
		display: 'flex',
		width: '100%',
		padding: token('space.200'),
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
		backgroundColor: token('elevation.surface'),
	},
	addCommentClosedContainerStyles: {
		width: '100%',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		flexShrink: 0,
	},
	addCommentReactionsContainerStyles: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
	},
	rightButtonContainerStyles: {
		alignItems: 'center',
	},
});

export const ObjectAddCommentContainerComponent = ({
	showReactions,
	showLabels,
	isOffline,
	optimisticReactions = false,
}: {
	showReactions: boolean;
	showLabels: boolean;
	isOffline?: boolean;
	optimisticReactions?: boolean;
}): React.JSX.Element => {
	const shouldAutofocusPromptRef = useRef<boolean>(false);
	const { expandCommentContainer, setFooterEditorEntry } = useObjectCommentDispatchContext();
	const { expanded } = useObjectCommentContext();

	const [contentId] = usePageContentId();

	const objectAddCommentContainerId = 'addCommentButton';

	const handleExpandCommentContainer = useCallback(() => {
		expandCommentContainer({
			entryPoint: 'pageFooter',
		});
		setFooterEditorEntry(AddFooterCommentMode.FOOTER);
	}, [expandCommentContainer, setFooterEditorEntry]);

	return (
		<>
			<Box
				id={objectAddCommentContainerId}
				xcss={styles.addCommentContainerStyles}
				testId={objectAddCommentContainerId}
			>
				{!expanded && (
					<Box xcss={styles.addCommentClosedContainerStyles}>
						<ObjectAddCommentPrompt
							onClick={handleExpandCommentContainer}
							autofocusRef={shouldAutofocusPromptRef}
						/>
						<Flex
							gap="space.050"
							testId="objectAddCommentContainer-rightButtonContainer"
							xcss={styles.rightButtonContainerStyles}
						>
							{showLabels && fg('confluence_frontend_labels_in_footer') && (
								<>
									<ExperienceStart id={contentId} name={VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE} />
									<FooterLabelsLoader />
								</>
							)}
							{showReactions && (
								<Box xcss={styles.addCommentReactionsContainerStyles}>
									<ObjectCommentPageReactions
										isOffline={isOffline}
										optimisticReactions={optimisticReactions}
									/>
								</Box>
							)}
						</Flex>
					</Box>
				)}
			</Box>
		</>
	);
};

export const ObjectAddCommentContainer: React.FC<{
	showReactions: boolean;
	showLabels: boolean;
	isOffline?: boolean;
	optimisticReactions?: boolean;
}> = withErrorBoundary({
	attribution: Attribution.COMMENTS,
})(ObjectAddCommentContainerComponent);
